const identity = (value) => String(value ?? '').trim();

const actorIdentities = (actor) => [
  actor?.id,
  actor?.combatantId,
  // Template IDs identify content, never combatant instances. date_of_change: 2026-09-05.

].map(identity).filter(Boolean);

/**
 * Rebuild the InitiativeTracker payload from a joined authoritative scene.
 * Live initiative events already carry full combatants, but a reload receives
 * order/turn state and tokens in the scene snapshot. date_of_change: 2026-08-26
 */
export const buildAuthoritativeInitiativeHydration = (state, tokens) => {
  const order = Array.isArray(state?.initiativeOrder)
    ? state.initiativeOrder.map(identity).filter(Boolean)
    : [];
  if (order.length === 0) return null;

  const availableTokens = Array.isArray(tokens) ? tokens : [];
  const storedCombatants = Array.isArray(state?.combatants) ? state.combatants : [];
  const sceneTokens = Array.isArray(state?.map?.tokens) ? state.map.tokens : [];
  const findActor = (actorId) => {
    const token = availableTokens.find((candidate) => actorIdentities(candidate).includes(actorId));
    const stored = storedCombatants.find((candidate) => actorIdentities(candidate).includes(actorId));
    const saved = sceneTokens.find((candidate) => actorIdentities(candidate).includes(actorId));
    if (!token && !stored) return null;
    // D&D Basic Rules, Initiative. A sheet modifier must never replace the
    // room's rolled result when the drawer is rebuilt. date_of_change: 2026-11-04
    const initiativeRoll = saved?.runtimeOverlay?.initiativeRoll ?? saved?.initiativeRoll
      ?? token?.initiativeRoll ?? stored?.initiativeRoll;
    return {
      ...(stored || {}),
      ...(token || {}),
      id: token?.id || stored?.id || actorId,
      name: stored?.name || token?.name || 'Combatant',
      initiativeRoll,
      initiative: initiativeRoll?.total ?? stored?.initiative ?? token?.initiative ?? 0,
    };
  };
  const combatants = order.map(findActor);
  // Wait for the routed token snapshot instead of applying a partial turn
  // order during the join/render race.
  if (combatants.some((combatant) => combatant === null)) return null;

  const activeCombatantId = identity(state?.activeCombatant) || null;
  const activeIndex = activeCombatantId
    ? order.findIndex((actorId) => actorId === activeCombatantId)
    : -1;
  const requestedTurn = Number(state?.turn);
  const fallbackTurn = Number.isInteger(requestedTurn) && requestedTurn >= 0
    ? Math.min(requestedTurn, combatants.length - 1)
    : 0;
  const currentTurnIndex = activeIndex >= 0 ? activeIndex : fallbackTurn;
  const combatStarted = state?.combatStarted === true;

  return {
    combatants,
    currentTurnIndex,
    activeCombatantId: combatStarted && activeIndex >= 0 ? combatants[activeIndex].id : null,
    round: Math.max(1, Number(state?.round) || 1),
    combatStarted,
    isInitiativeConfirmed: combatStarted,
  };
};

export const authoritativeInitiativeSignature = (payload) => payload
  ? [
      payload.combatStarted ? 'started' : 'pending',
      payload.round,
      payload.currentTurnIndex,
      payload.activeCombatantId || 'none',
      payload.combatants.map((combatant) => `${combatant.id}:${combatant.initiative}`).join('|'),
    ].join('::')
  : null;
